import styled from 'styled-components';

/** Temas com UI escura — `white01` continua #fff no token, então o calendário usa superfícies próprias. */
export function isCalendarDarkTheme(theme: { title?: string } | undefined): boolean {
  return theme?.title === 'dark' || theme?.title === 'contrast';
}

/** Fundo de célula / card no calendário (claro vs escuro). */
function calendarCellSurface(theme: { title?: string; colors?: { white01?: string; gray07?: string } }): string {
  const c = theme.colors;
  if (!c) return '#ffffff';
  return isCalendarDarkTheme(theme) ? (c.gray07 ?? '#333333') : (c.white01 ?? '#ffffff');
}

/** Alinhado ao padrão de cards do portal (ex.: Minhas Notas — DivName). */
export const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  width: 100%;
`;

export const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

export const CalendarCard = styled.div`
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors?.gray03};
  background: ${({ theme }) =>
    isCalendarDarkTheme(theme)
      ? theme.colors?.gray07 ?? '#333333'
      : theme.colors?.white01 ?? '#ffffff'};
  padding: 1.25rem 1rem 1.5rem;
  width: 100%;

  @media screen and (min-width: 768px) {
    padding: 1.5rem 1.5rem 1.75rem;
  }
`;

export const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  justify-content: space-between;
  gap: 1rem;
`;

export const ToolbarLeft = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: center;
  flex: 1;
  min-width: min(100%, 280px);
`;

export const SelectField = styled.select`
  padding: 0.5rem 0.75rem;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors?.gray03};
  font-size: ${({ theme }) => theme.fontSize[theme.fontSizeType].fs14};
  color: ${({ theme }) => theme.colors?.text?.primary};
  background: ${({ theme }) =>
    isCalendarDarkTheme(theme)
      ? theme.colors?.gray07 ?? '#333333'
      : theme.colors?.white01 ?? '#ffffff'};
  min-width: min(160px, 100%);
  flex: 1 1 auto;
  max-width: 100%;

  &:disabled {
    opacity: 0.75;
    cursor: not-allowed;
  }
`;

export const MonthNav = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.65rem;
  padding-bottom: 0.75rem;
  margin-bottom: 0.25rem;
  margin-top: 2rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors?.gray02 ?? theme.colors?.gray06};

  @media screen and (min-width: 768px) {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
    align-items: center;
    gap: 0.75rem 1rem;
    flex-direction: unset;
  }
`;

export const MonthNavRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem 0.75rem;
  width: 100%;

  @media screen and (min-width: 768px) {
    display: contents;
  }
`;

export const MonthTitle = styled.h2`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize[theme.fontSizeType].fs18};
  font-weight: 600;
  color: ${({ theme }) => theme.colors?.text?.primary};
  text-align: center;
  width: 100%;
  text-transform: capitalize;

  @media screen and (min-width: 768px) {
    font-size: ${({ theme }) => theme.fontSize[theme.fontSizeType].fs20};
    grid-column: 2;
    grid-row: 1;
    width: auto;
    justify-self: center;
  }
`;

export const NavLink = styled.button<{ $alignEnd?: boolean }>`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors?.secondary};
  cursor: pointer;
  font-size: ${({ theme }) => theme.fontSize[theme.fontSizeType].fs14};
  padding: 0.35rem 0.5rem;
  text-align: ${({ $alignEnd }) => ($alignEnd ? 'right' : 'left')};
  max-width: calc(50% - 0.35rem);
  line-height: 1.3;
  flex: 1 1 0;
  min-width: 0;

  &:hover {
    text-decoration: underline;
  }

  @media screen and (max-width: 480px) {
    font-size: ${({ theme }) => theme.fontSize[theme.fontSizeType].fs12};
  }

  @media screen and (min-width: 768px) {
    max-width: none;
    flex: none;
    min-width: 0;

    &:first-of-type {
      grid-column: 1;
      grid-row: 1;
      justify-self: start;
      text-align: left;
    }

    &:last-of-type {
      grid-column: 3;
      grid-row: 1;
      justify-self: end;
      text-align: right;
    }
  }
`;

/** Mês abreviado acima da grade (ex.: MAR.), como referência visual. */
export const GridMonthRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  padding: 0 0.125rem;
`;

export const MonthAbbrev = styled.span`
  font-size: ${({ theme }) => theme.fontSize[theme.fontSizeType].fs24};
  font-weight: 700;
  color: ${({ theme }) => theme.colors?.text?.primary};
  letter-spacing: 0.02em;
`;

export const MonthAbbrevYear = styled.span`
  font-size: ${({ theme }) => theme.fontSize[theme.fontSizeType].fs14};
  font-weight: 500;
  color: ${({ theme }) => theme.colors?.text?.secondary};
`;

export const GridScroll = styled.div`
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

export const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 3px;
  min-width: 100%;
  background: ${({ theme }) => theme.colors?.gray02 ?? theme.colors?.gray06};
  border-radius: 10px;
  padding: 3px;
`;

export const WeekdayHeader = styled.div<{ $sunday?: boolean }>`
  padding: 0.35rem 0.15rem;
  text-align: center;
  font-size: ${({ theme }) => theme.fontSize[theme.fontSizeType].fs12};
  font-weight: 700;
  color: ${({ $sunday, theme }) =>
    $sunday ? (theme.colors?.danger02 ?? '#c62828') : theme.colors?.text?.secondary};
  background: ${({ theme }) => theme.colors?.gray01};
  border-radius: 6px 6px 0 0;
`;

export const DayCell = styled.div<{
  $muted?: boolean;
  $hasActivity?: boolean;
}>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  min-height: 0;
  aspect-ratio: 1;
  max-height: 5.5rem;
  padding: 0.35rem 0.2rem 0.45rem;
  background: ${({ $muted, $hasActivity, theme }) => {
    const c = theme.colors;
    const p = c?.primary ?? '#6a1b9a';
    const base = calendarCellSurface(theme);
    const mutedBg = c?.gray01 ?? '#f2f2f2';
    if ($muted) return mutedBg;
    if ($hasActivity)
      return `linear-gradient(165deg, ${p}33 0%, ${base} 55%, ${base} 100%)`;
    return base;
  }};
  opacity: ${({ $muted }) => ($muted ? 0.55 : 1)};
  border-radius: 8px;
  overflow: hidden;
  box-sizing: border-box;
  width: 100%;

  @media screen and (min-width: 480px) {
    max-height: 6.5rem;
    padding: 0.45rem 0.25rem 0.5rem;
  }

  @media screen and (min-width: 900px) {
    max-height: 7.5rem;
  }

  @media screen and (min-width: ${({ theme }) => theme.size?.xxlarge ?? '1201px'}) {
    aspect-ratio: unset;
    max-height: none;
    min-height: 6.25rem;
    align-items: stretch;
  }
`;

export const DayCellButton = styled.button<{
  $muted?: boolean;
  $hasActivity?: boolean;
}>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  min-height: 0;
  aspect-ratio: 1;
  max-height: 5.5rem;
  padding: 0.35rem 0.2rem 0.45rem;
  background: ${({ $muted, $hasActivity, theme }) => {
    const c = theme.colors;
    const p = c?.primary ?? '#6a1b9a';
    const base = calendarCellSurface(theme);
    const g = c?.gray01 ?? '#f5f5f5';
    if ($muted && !$hasActivity) return g;
    if ($muted && $hasActivity)
      return `linear-gradient(165deg, ${p}30 0%, ${g} 55%, ${g} 100%)`;
    if ($hasActivity)
      return `linear-gradient(165deg, ${p}33 0%, ${base} 55%, ${base} 100%)`;
    return base;
  }};
  opacity: ${({ $muted }) => ($muted ? 0.72 : 1)};
  border-radius: 8px;
  overflow: hidden;
  box-sizing: border-box;
  width: 100%;
  border: none;
  font: inherit;
  cursor: pointer;
  -webkit-tap-highlight-color: ${({ theme }) => theme.colors?.disable01 ?? 'rgba(0,0,0,0.08)'};

  @media screen and (min-width: 480px) {
    max-height: 6.5rem;
    padding: 0.45rem 0.25rem 0.5rem;
  }

  @media screen and (min-width: 900px) {
    max-height: 7.5rem;
  }

  @media screen and (min-width: ${({ theme }) => theme.size?.xxlarge ?? '1201px'}) {
    aspect-ratio: unset;
    max-height: none;
    min-height: 6.25rem;
    align-items: stretch;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors?.secondary};
    outline-offset: 2px;
  }

  &:active:not(:disabled) {
    filter: brightness(0.96);
  }
`;

/** Número do dia: “hoje” preenchido; dia selecionado (modal aberto) com borda. */
export const DayNumberWrap = styled.span<{
  $isToday?: boolean;
  $isSelected?: boolean;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.65rem;
  height: 1.65rem;
  padding: 0 0.35rem;
  border-radius: 8px;
  font-size: ${({ theme }) => theme.fontSize[theme.fontSizeType].fs12};
  font-weight: 700;
  line-height: 1;
  color: ${({ $isToday, theme }) =>
    $isToday ? (theme.colors?.text?.tertiary ?? '#fff') : theme.colors?.text?.primary};
  background: ${({ $isToday, theme }) =>
    $isToday ? (theme.colors?.secondary ?? theme.colors?.primary ?? '#47AAEB') : 'transparent'};
  box-shadow: ${({ $isSelected, $isToday, theme }) => {
    const ring = theme.colors?.text?.primary ?? '#333';
    const innerRing = isCalendarDarkTheme(theme)
      ? theme.colors?.gray05 ?? '#3f3f3f'
      : theme.colors?.white01 ?? '#ffffff';
    if ($isSelected && $isToday) {
      return `0 0 0 2px ${innerRing}, 0 0 0 4px ${ring}`;
    }
    if ($isSelected) return `0 0 0 2px ${ring}`;
    return 'none';
  }};

  @media screen and (min-width: 480px) {
    min-width: 1.85rem;
    height: 1.85rem;
    font-size: ${({ theme }) => theme.fontSize[theme.fontSizeType].fs14};
  }
`;

/** Celular/tablet: traços coloridos por atividade. */
export const ActivityBars = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  width: 100%;
  margin-top: auto;
  padding-top: 4px;
  flex-shrink: 0;

  @media screen and (min-width: ${({ theme }) => theme.size?.xxlarge ?? '1201px'}) {
    display: none;
  }
`;

export const ActivityBarSegment = styled.span<{ $variant: 'start' | 'end' }>`
  display: block;
  width: 100%;
  height: 4px;
  border-radius: 2px;
  background: ${({ $variant, theme }) =>
    $variant === 'start'
      ? (theme.colors?.primary ?? '#6a1b9a')
      : (theme.colors?.attention ?? theme.colors?.secondary ?? '#9c27b0')};
  opacity: 0.92;
`;

/** Desktop: lista com fundo colorido + texto (primeira versão), sem traços vazios. */
export const DesktopEventsStack = styled.div`
  display: none;
  width: 100%;
  margin-top: auto;
  padding-top: 4px;
  flex: 1;
  min-height: 0;
  flex-direction: column;
  gap: 3px;
  overflow-y: auto;
  overflow-x: hidden;
  flex-shrink: 0;

  scrollbar-width: thin;
  scrollbar-color: ${({ theme }) => theme.colors?.gray03} transparent;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors?.gray03};
    border-radius: 4px;
  }

  @media screen and (min-width: ${({ theme }) => theme.size?.xxlarge ?? '1201px'}) {
    display: flex;
  }
`;

export const DesktopEventPill = styled.div<{ $kind: 'start' | 'end' }>`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  width: 100%;
  padding: 3px 5px;
  border-radius: 6px;
  font-size: ${({ theme }) => theme.fontSize[theme.fontSizeType].fs10};
  line-height: 1.2;
  color: ${({ theme }) => theme.colors?.text?.tertiary ?? '#fff'};
  background: ${({ $kind, theme }) =>
    $kind === 'start'
      ? theme.colors?.primary
      : (theme.colors?.attention ?? theme.colors?.text?.attention)};
  overflow: hidden;
  cursor: default;

  @media screen and (min-width: ${({ theme }) => theme.size?.xlarge ?? '1025px'}) {
    font-size: ${({ theme }) => theme.fontSize[theme.fontSizeType].fs12};
    padding: 4px 6px;
  }
`;

export const DesktopEventPillText = styled.span`
  min-width: 0;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const DesktopEventIcon = styled.span`
  width: 6px;
  height: 6px;
  flex-shrink: 0;
  background: ${({ theme }) => theme.colors?.danger02 ?? '#c62828'};
  border-radius: 1px;
`;

export const ModalEmptyHint = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize[theme.fontSizeType].fs14};
  color: ${({ theme }) => theme.colors?.text?.secondary};
  line-height: 1.5;
`;

export const ModalTitleRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.35rem;
  flex-wrap: wrap;
  padding-right: 2.5rem;
`;

export const ModalTitleDay = styled.span`
  font-size: 2.25rem;
  font-weight: 800;
  line-height: 1;
  color: ${({ theme }) => theme.colors?.text?.primary};
`;

export const ModalTitleWeekday = styled.span`
  font-size: 1.35rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.colors?.text?.primary};
`;

export const ModalDateSub = styled.p`
  margin: 0.35rem 0 0;
  font-size: ${({ theme }) => theme.fontSize[theme.fontSizeType].fs12};
  font-weight: 500;
  color: ${({ theme }) => theme.colors?.text?.secondary};
  text-transform: capitalize;
`;

export const ModalTodayBadge = styled.span`
  font-size: ${({ theme }) => theme.fontSize[theme.fontSizeType].fs14};
  font-weight: 600;
  color: ${({ theme }) => theme.colors?.secondary};
  margin-left: 0.15rem;
`;

export const ModalEventList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

export const ModalEventItem = styled.li<{ $kind: 'start' | 'end' }>`
  padding: 0.85rem 0;
  border-bottom: 1px solid
    ${({ theme }) =>
      isCalendarDarkTheme(theme)
        ? theme.colors?.gray03
        : theme.colors?.gray02 ?? theme.colors?.gray06};

  &:last-child {
    border-bottom: none;
  }

  border-left: 3px solid
    ${({ $kind, theme }) =>
      $kind === 'start'
        ? (theme.colors?.primary ?? '#6a1b9a')
        : (theme.colors?.attention ?? theme.colors?.secondary ?? '#9c27b0')};
  padding-left: 0.75rem;
  margin-left: 0;
`;

export const ModalEventKind = styled.span<{ $kind: 'start' | 'end' }>`
  display: inline-block;
  font-size: ${({ theme }) => theme.fontSize[theme.fontSizeType].fs10};
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors?.text?.tertiary ?? '#fff'};
  padding: 0.2rem 0.5rem;
  border-radius: 6px;
  margin-bottom: 0.4rem;
  background: ${({ $kind, theme }) =>
    $kind === 'start'
      ? theme.colors?.primary
      : (theme.colors?.attention ?? theme.colors?.text?.attention)};
`;

export const ModalEventTitle = styled.p`
  margin: 0 0 0.3rem;
  font-size: ${({ theme }) => theme.fontSize[theme.fontSizeType].fs14};
  font-weight: 600;
  color: ${({ theme }) => theme.colors?.text?.primary};
  line-height: 1.35;
  word-break: break-word;
`;

export const ModalEventMeta = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize[theme.fontSizeType].fs12};
  color: ${({ theme }) => theme.colors?.text?.secondary};
  line-height: 1.4;
  word-break: break-word;

  & + & {
    margin-top: 0.2rem;
  }
`;

/** Vista “Lista”: dias com atividades em sequência (select Visualização → Lista). */
export const ListViewContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 0.25rem;
`;

export const ListDayBlock = styled.button`
  display: block;
  width: 100%;
  margin: 0;
  padding: 0;
  border: 1px solid ${({ theme }) => theme.colors?.gray03};
  border-radius: 12px;
  overflow: hidden;
  background: ${({ theme }) =>
    isCalendarDarkTheme(theme)
      ? theme.colors?.gray07 ?? '#333333'
      : theme.colors?.white01 ?? '#ffffff'};
  cursor: pointer;
  text-align: left;
  font: inherit;
  -webkit-tap-highlight-color: ${({ theme }) => theme.colors?.disable01 ?? 'rgba(0,0,0,0.08)'};

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors?.secondary};
    outline-offset: 2px;
  }

  &:active:not(:disabled) {
    filter: brightness(0.99);
  }
`;

export const ListDayHeader = styled.div`
  padding: 0.6rem 0.85rem;
  background: ${({ theme }) => theme.colors?.gray01};
  font-size: ${({ theme }) => theme.fontSize[theme.fontSizeType].fs14};
  font-weight: 600;
  color: ${({ theme }) => theme.colors?.primary};
  text-transform: capitalize;
  line-height: 1.35;
`;

export const ListEmptyHint = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize[theme.fontSizeType].fs14};
  color: ${({ theme }) => theme.colors?.text?.secondary};
  line-height: 1.45;
`;

export const Breadcrumb = styled.nav`
  font-size: ${({ theme }) => theme.fontSize[theme.fontSizeType].fs12};
  color: ${({ theme }) => theme.colors?.text?.secondary};
  line-height: 1.4;

  span {
    margin: 0 0.2rem;
  }
`;
